import type { MessageDescriptor } from "@lingui/core"
import { msg } from "@lingui/core/macro"

/**
 * Human copy ONLY for the Model-selection UI.
 *
 * All STRUCTURAL data — which tiers exist, which tier an action belongs to, each
 * tier's floor/recommended model, and what runs right now — comes live from the
 * `defaults` payload of `GET /settings/models` (and `/api/admin/models`),
 * computed server-side from the same resolver the pipeline uses. Nothing
 * structural is duplicated here, so the "Automatic" labels can never drift from
 * what actually runs. This file only supplies plain-language labels/descriptions
 * keyed by the string ids the endpoint returns, with a graceful fallback
 * ({@link humanizeId}) for any id there's no copy for yet.
 */

/** Sentinel Select value meaning "no explicit choice" — clears the setting key. */
export const INHERIT_VALUE = "__inherit__"

/** The per-task override setting key for an action (`task_model_<action>`). */
export function taskModelKey(action: string): string {
  return `task_model_${action}`
}

/** Graceful fallback label for an id with no catalogued copy: "final_analysis" → "Final analysis". */
export function humanizeId(id: string): string {
  const spaced = id.replace(/_/g, " ")
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export interface ITierCopy {
  /** Short tier name shown as the picker heading. */
  label: MessageDescriptor
  /** One plain sentence telling a non-technical admin what riding this tier costs
   *  them in quality, so they can judge where a cheaper model is safe. */
  consequence: MessageDescriptor
}

/**
 * Plain copy per tier id (keys mirror the endpoint's tier strings). Copy only —
 * the tier LIST + order + configured/floor models come from the endpoint.
 */
export const TIER_COPY: Record<string, ITierCopy> = {
  light: {
    label: msg`Light`,
    consequence: msg`High-volume background work — live notes, chapter boundaries, jargon. A smaller model here cuts most of your credit burn and is barely noticeable.`,
  },
  standard: {
    label: msg`Default`,
    consequence: msg`Your meeting summaries and answers — the quality here is what you read every day.`,
  },
  heavy: {
    label: msg`Heavy`,
    consequence: msg`The optional premium model, used only when someone upgrades a one-off report.`,
  },
}

export interface ITaskCopy {
  /** Friendly task name (the row's primary label). */
  label: MessageDescriptor
  /** Plain description of what this step does. */
  description: MessageDescriptor
  /** Honest quality-sensitivity hint — is a cheaper model fine here, or visible? */
  sensitivity: MessageDescriptor
}

/**
 * Per-task plain-language copy, keyed by the action string the endpoint returns.
 * `sensitivity` mirrors the owner's ask: forgiving tasks say a cheap model is
 * fine, sensitive tasks warn that a weaker model shows in what people read. An
 * action with no entry here falls back to {@link humanizeId} (label only).
 */
export const TASK_COPY: Record<string, ITaskCopy> = {
  quick_analysis: {
    label: msg`Live notes`,
    description: msg`Running takes and highlights while a meeting is still in progress.`,
    sensitivity: msg`High volume, low stakes — a cheap model keeps up fine.`,
  },
  jargon_detection: {
    label: msg`Jargon detection`,
    description: msg`Spotting acronyms and domain terms worth defining.`,
    sensitivity: msg`Simple spotting work — a cheap model is plenty.`,
  },
  translation: {
    label: msg`Translation`,
    description: msg`Translating a finished summary into another language.`,
    sensitivity: msg`Small models translate well — rarely worth a pricier one.`,
  },
  classify_report_fit: {
    label: msg`Report matching`,
    description: msg`Guessing which report type a meeting is a good fit for.`,
    sensitivity: msg`A quick judgement call — a cheap model is fine here.`,
  },
  transcript_clean: {
    label: msg`Transcript clean-up`,
    description: msg`Tidying filler words and stumbles out of the raw transcript.`,
    sensitivity: msg`Mechanical clean-up — a cheap model won't be noticed.`,
  },
  chapter_clustering: {
    label: msg`Chapter detection`,
    description: msg`Splitting a long meeting into chapters and sections.`,
    sensitivity: msg`Forgiving, but going too cheap can mis-split — the default keeps a mid-range model here.`,
  },
  speaker_resolution: {
    label: msg`Speaker matching`,
    description: msg`Working out who said what across the meeting.`,
    sensitivity: msg`Mistakes surface in the summary — worth a capable model.`,
  },
  final_analysis: {
    label: msg`Meeting summary`,
    description: msg`Your headline summary — decisions, action points, and highlights.`,
    sensitivity: msg`This is what you read every day. A weaker model is clearly visible.`,
  },
  meeting_qa: {
    label: msg`Ask answers`,
    description: msg`Answering the questions you ask about a meeting.`,
    sensitivity: msg`Answers are read straight off — a weaker model shows.`,
  },
  generate_report: {
    label: msg`Report writing`,
    description: msg`Building a full visual report from the meeting.`,
    sensitivity: msg`Long-form synthesis — a weaker model reads flat.`,
  },
  material_extract: {
    label: msg`File extract`,
    description: msg`Reading a whiteboard photo or attached document so search and notes can use it.`,
    sensitivity: msg`High volume, mechanical — a cheap model is fine.`,
  },
  material_digitize: {
    label: msg`Diagram from photo`,
    description: msg`Turning a whiteboard photo into a diagram.`,
    sensitivity: msg`The diagram is read — worth a capable model.`,
  },
}

/** Friendly, jargon-free label per capability band from the models endpoint. */
export const CAPABILITY_LABEL: Record<"fast" | "balanced" | "most-capable" | "unknown", MessageDescriptor> = {
  fast: msg`Fast`,
  balanced: msg`Balanced`,
  "most-capable": msg`Most capable`,
  unknown: msg`Not priced yet`,
}

/** Provider group headings for the grouped model picker. */
export const PROVIDER_LABEL: Record<"anthropic" | "openai" | "openrouter", string> = {
  anthropic: "Anthropic (Claude)",
  openai: "OpenAI",
  openrouter: "OpenRouter",
}
